import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";
import type { InstructorCourse, CoursePricingType } from "services/instructorCoursesService";

export const DEFAULT_BUNDLE_CURRENCY = "EGP";

export type InstructorBundle = {
  id: number;
  title: string;
  description?: string | null;
  image?: string | null;
  image_url?: string | null;
  pricing_type?: CoursePricingType;
  is_free?: boolean;
  is_paid?: boolean;
  price?: number | null;
  currency?: string | null;
  original_total_price?: number;
  discount_amount?: number;
  discount_percentage?: number;
  courses_count?: number;
  is_active?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  created_by?: number;
  owner_name?: string | null;
  courses?: InstructorCourse[];
  created_at?: string;
  updated_at?: string;
};

export type BundleUpsertBody = {
  title: string;
  description?: string | null;
  image?: string | null;
  pricing_type?: CoursePricingType;
  price?: number | null;
  currency?: string | null;
  is_active?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  course_ids: number[];
};

export type BundlesListQuery = {
  page?: number;
  limit?: number;
  term?: string;
  is_active?: boolean;
};

export const instructorBundlesService = {
  async list(params: BundlesListQuery = {}) {
    const res = await api.get<ApiEnvelope<PaginatedResult<InstructorBundle>>>("/instructors/course-bundles", {
      params,
      meta: { toastSuccess: false },
    });
    return unwrapList<InstructorBundle>(res.data as any);
  },

  async get(bundleId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: InstructorBundle }>>(`/instructors/course-bundles/${bundleId}`, {
      meta: { toastSuccess: false },
    });
    return unwrapData(res.data);
  },

  async create(body: BundleUpsertBody) {
    const res = await api.post<ApiEnvelope<{ data: InstructorBundle }>>("/instructors/course-bundles", body);
    return unwrapData(res.data);
  },

  async update(bundleId: number | string, body: Partial<BundleUpsertBody>) {
    const res = await api.put<ApiEnvelope<{ data: InstructorBundle }>>(`/instructors/course-bundles/${bundleId}`, body);
    return unwrapData(res.data);
  },

  async setStatus(bundleId: number | string, isActive: boolean) {
    const res = await api.patch<ApiEnvelope<{ data: InstructorBundle }>>(
      `/instructors/course-bundles/${bundleId}/status`,
      { is_active: isActive }
    );
    return unwrapData(res.data);
  },

  async remove(bundleId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(`/instructors/course-bundles/${bundleId}`);
    return { message: (res.data as any)?.message ?? "Deleted." };
  },
};
